import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { classNames } from '../utils/themeHelpers';
import LicenseGate from '../components/ui/LicenseGate';
import { ChartGuideModal } from '../components/ui';
import useChartGuide from '../hooks/useChartGuide';
import { HeatMap, PerformanceSummary, PerformanceTrendAnalysis, EvaluationDashboardGuide } from '../components/smartEvaluation';
import { EvaluationMetrics } from '../types/evaluation';
import { useEvaluation12MonthAverage } from '../hooks/useEvaluationQueries';

interface Brand {
  id: number;
  name: string;
  logo?: string;
}

interface Branch {
  id: number;
  name: string;
  brandId: number;
  logo?: string;
}

interface Props {
  selectedBranch?: Branch | null;
  selectedBrand?: Brand | null;
}

const SmartEvaluationReportExactMatch: React.FC<Props> = ({ selectedBranch, selectedBrand }) => {
  const { currentTheme } = useTheme();
  const { isModalOpen, currentGuide, closeGuide } = useChartGuide();
  
  // Fetch 12-month average data
  const { data: average12MonthData, isLoading: isLoading12Month } = useEvaluation12MonthAverage(selectedBranch?.id || 0);
  
  // Define metrics for evaluation
  const metrics = [
    { id: "اشتراک_گیری", name: "اشتراک‌گیری", unit: "%", type: "percent", isSelected: true },
    { id: "درصد_نظر_راضی", name: "درصد نظرات مثبت", unit: "%", type: "percent", isSelected: false },
    { id: "مشارکت_در_نظرسنجی", name: "مشارکت در نظرسنجی", unit: "%", type: "percent", isSelected: false },
    { id: "تعداد_روز_بدون_شارژ_پیامک", name: "روزهای بدون شارژ پیامک", unit: "", type: "count", isSelected: false }
  ];
  
  // State for selected metric in the heatmap
  const [selectedMetric, setSelectedMetric] = useState(metrics[0].id);
  // State for detailed view month
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  // State for selected month data
  const [selectedMonthData, setSelectedMonthData] = useState<EvaluationMetrics | null>(null);
  // State for showing brand group line (visible by default for instant comparison context)
  const [showBrandLine, setShowBrandLine] = useState(true);
  // State for showing all restaurants line
  const [showAllRestaurantsLine, setShowAllRestaurantsLine] = useState(false);
  // State for showing 12 months average line (visible by default for instant comparison context)
  const [show12MonthsAverageLine, setShow12MonthsAverageLine] = useState(true);
  // State for comparison source (all restaurants, brand group, or 12 months average) - used for heatmap
  const [comparisonSource, setComparisonSource] = useState<'brand' | 'all' | 'average12'>('brand');

  // Function to handle metric selection
  const handleMetricSelect = (metricId: string) => {
    setSelectedMetric(metricId);
    setSelectedMonth(null);
  };

  // Function to handle comparison source change
  const handleComparisonSourceChange = (source: string) => {
    setComparisonSource(source as 'brand' | 'all' | 'average12');
  };

  // Function to toggle brand line
  const toggleBrandLine = () => {
    setShowBrandLine(!showBrandLine);
  };

  // Function to toggle 12 months average line
  const toggle12MonthsAverageLine = () => {
    setShow12MonthsAverageLine(!show12MonthsAverageLine);
  };

  // Function to handle month selection from heatmap
  const handleMonthSelect = (month: string, data: EvaluationMetrics | null) => {
    setSelectedMonth(month);
    setSelectedMonthData(data);
  };

  return (
    <LicenseGate licenseType="evaluation" dashboardName="نقشه حرارتی ارزیابی هوشمند">
      <div dir="rtl" className="container-dashboard">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {/* Professional Dashboard Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="title-dashboard">
                نقشه حرارتی ارزیابی هوشمند
              </h1>
              <p className="text-sm text-text-secondary mt-2">
                شدت رنگ‌ها نشان‌دهنده‌ی مقدار هر شاخص در هر ماه است.
              </p>
            </div>
            <EvaluationDashboardGuide
              brandName={selectedBrand?.name}
              className="self-start shrink-0"
            />
          </div>
          
          {/* Dashboard Controls - Three Separate Controllers Side by Side */}
          <div id="dashboard-controls" className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6" aria-label="کنترل‌های داشبورد">
            {/* Controller 1: Metric Selector */}
            <div className="rounded-xl bg-bg-secondary/60 backdrop-blur-sm border border-border-primary shadow-sm p-4">
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold text-text-primary">انتخاب شاخص</span>
                <span className="text-[11px] text-text-tertiary leading-relaxed">شاخصی که در نقشه حرارتی و نمودار نمایش داده می‌شود</span>
                <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="انتخاب شاخص">
                  {metrics.map((metric) => (
                    <button
                      key={metric.id}
                      type="button"
                      onClick={() => handleMetricSelect(metric.id)}
                      role="radio"
                      aria-checked={selectedMetric === metric.id}
                      className={classNames(
                        "px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-base border",
                        selectedMetric === metric.id
                          ? "bg-primary-600 text-theme-inverse border-primary-600 shadow-sm"
                          : "bg-theme-secondary text-theme-secondary border-border-primary hover:text-theme-primary"
                      )}
                    >
                      {metric.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Controller 2: Comparison Source */}
            <div className="rounded-xl bg-bg-secondary/60 backdrop-blur-sm border border-border-primary shadow-sm p-4">
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold text-text-primary">مقایسه با</span>
                <span className="text-[11px] text-text-tertiary leading-relaxed">معیار مقایسه در نقشه حرارتی</span>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleComparisonSourceChange('brand')}
                    aria-pressed={comparisonSource === 'brand'}
                    className={classNames(
                      "px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-base border",
                      comparisonSource === 'brand'
                        ? "bg-primary-600 text-theme-inverse border-primary-600 shadow-sm"
                        : "bg-theme-secondary text-theme-secondary border-border-primary hover:text-theme-primary"
                    )}
                  >
                    گروه {selectedBrand?.name || 'برند'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleComparisonSourceChange('average12')}
                    aria-pressed={comparisonSource === 'average12'}
                    className={classNames(
                      "px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-base border",
                      comparisonSource === 'average12'
                        ? "bg-primary-600 text-theme-inverse border-primary-600 shadow-sm"
                        : "bg-theme-secondary text-theme-secondary border-border-primary hover:text-theme-primary"
                    )}
                  >
                    میانگین ۱۲ ماه
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Professional Heat Map Container */}
            <div id="heatmap" className="lg:col-span-2 order-2 lg:order-1 space-y-6 md:space-y-8">
              <HeatMap
                branchId={selectedBranch?.id}
                selectedMetric={selectedMetric}
                comparisonSource={comparisonSource}
                onMonthSelect={handleMonthSelect}
                metrics={metrics}
                average12MonthData={average12MonthData?.averages}
              />
              
              {/* Chart Options - placed next to the trend chart it controls */}
              <div className="rounded-xl bg-bg-secondary/60 backdrop-blur-sm border border-border-primary shadow-sm p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <span className="text-xs font-semibold text-text-primary shrink-0">خطوط نمودار:</span>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={toggleBrandLine}
                      aria-pressed={showBrandLine}
                      aria-label="نمایش/مخفی کردن خط برند"
                      className={classNames(
                        "px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-base border",
                        showBrandLine
                          ? "bg-primary-600 text-theme-inverse border-primary-600 shadow-sm"
                          : "bg-theme-secondary text-theme-secondary border-border-primary hover:text-theme-primary"
                      )}
                    >
                      خط برند
                    </button>
                    <button
                      type="button"
                      onClick={toggle12MonthsAverageLine}
                      aria-pressed={show12MonthsAverageLine}
                      aria-label="نمایش/مخفی کردن خط میانگین ۱۲ ماه"
                      className={classNames(
                        "px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-base border",
                        show12MonthsAverageLine
                          ? "bg-primary-600 text-theme-inverse border-primary-600 shadow-sm"
                          : "bg-theme-secondary text-theme-secondary border-border-primary hover:text-theme-primary"
                      )}
                    >
                      میانگین ۱۲ ماه
                    </button>
                  </div>
                </div>
              </div>

              <PerformanceTrendAnalysis
                branchId={selectedBranch?.id}
                branchName={selectedBranch?.name}
                brandName={selectedBrand?.name}
                selectedMetric={selectedMetric}
                showBrandLine={showBrandLine}
                showAllRestaurantsLine={showAllRestaurantsLine}
                show12MonthsAverageLine={show12MonthsAverageLine}
                metrics={metrics}
                average12MonthData={average12MonthData?.averages}
              />
            </div>

            {/* Professional Analysis Panel */}
            <div id="monthly-details" className="card-base order-1 lg:order-2">
              <PerformanceSummary
                branchId={selectedBranch?.id}
                selectedMonth={selectedMonth}
                selectedMonthData={selectedMonthData}
              />
            </div>
          </div>
        </div>
        
        {currentGuide && (
          <ChartGuideModal
            isOpen={isModalOpen}
            onClose={closeGuide}
            title={currentGuide.title}
          >
            <div className="space-y-8">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">توضیحات</h3>
                <p className="text-text-secondary">{currentGuide.description}</p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نحوه تفسیر</h3>
                <p className="text-text-secondary">{currentGuide.interpretation}</p>
              </div>
              {currentGuide.tips.length > 0 && (
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نکات کلیدی</h3>
                  <ul className="list-disc list-inside space-y-2">
                    {currentGuide.tips.map((tip, index) => (
                      <li key={index} className="text-text-secondary">{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </ChartGuideModal>
        )}
      </div>
    </LicenseGate>
  );
};

export default SmartEvaluationReportExactMatch;